import VSpinner from '@voly/ui-components/src/v2/VSpinner.jsx';
import { ReactElement, useState } from 'react';
import { translate } from 'src/app/project/features/translate/translate';
import { TWorkspaceCustomFields } from 'src/app/project/modules/custom-fields/custom-fields.model';
import {
  buildPointFieldLayout,
  TCustomFieldPermissions,
} from 'src/app/project/modules/points/point-card/point-card-fields.utils';
import { TPointCustomFields } from 'src/app/project/modules/points/points.model';
import { EDateFormat } from 'src/app/project/shared/enums/date-format.enum';
import { TAllUsers } from 'src/app/project/view-models/user.view-model';
import { TPointCustomField } from 'src/app/project/view-models/custom-field-response-model';
import {
  collectMentionIds,
  dropFormulaFields,
  lockSubValuesFields,
  toCustomFieldsList,
  toChangedPointCustomField,
} from '../../utils/bulkCustomFields';
import { TLayoutConfig } from '../organisms/CustomFieldGroupsSettings';
import PointFields from '../organisms/PointFields';
import BulkEditDialog from './BulkEditDialog';

export type TBulkCustomFieldsDialogInputs = {
  layout: TLayoutConfig[] | undefined;
  workspaceCustomFields: TWorkspaceCustomFields;
  customFieldPermissions: TCustomFieldPermissions;
  canEditPoint: boolean;
  users: TAllUsers;
  dateFormat: EDateFormat;
};

type TBulkCustomFieldsDialogProps = TBulkCustomFieldsDialogInputs & {
  onClose: () => void;
  onSave: (customFieldsList: TPointCustomField[], mentionIds: string[]) => Promise<void>;
};

const ignoreSubValues = (): void => undefined;

const BulkCustomFieldsDialog = ({
  layout,
  workspaceCustomFields,
  customFieldPermissions,
  canEditPoint,
  users,
  dateFormat,
  onClose,
  onSave,
}: TBulkCustomFieldsDialogProps): ReactElement => {
  const [changedPointCustomFields, setChangedPointCustomFields] = useState<TPointCustomFields>({});

  const customFieldsList = toCustomFieldsList(changedPointCustomFields);

  const setFieldValue = (fieldId: string, value: string): void => {
    setChangedPointCustomFields((current) => ({
      ...current,
      [fieldId]: toChangedPointCustomField(workspaceCustomFields[fieldId], value),
    }));
  };

  const setFieldIds = (fieldId: string, selectedItemIds: string[]): void => {
    setChangedPointCustomFields((current) => ({ ...current, [fieldId]: { selectedItemIds } }));
  };

  const handleSave = (): Promise<void> =>
    onSave(customFieldsList, collectMentionIds(changedPointCustomFields, workspaceCustomFields));

  const renderBody = (): ReactElement => {
    if (!layout) {
      return <VSpinner size="sm" color="fg.muted" />;
    }

    const fields = buildPointFieldLayout({
      fieldsLayout: layout,
      workspaceCustomFields,
      pointCustomFields: changedPointCustomFields,
      canEditPoint,
      customFieldPermissions,
      onSave: setFieldValue,
      onSaveIds: setFieldIds,
      onSaveSubValues: ignoreSubValues,
    });

    return (
      <PointFields
        fields={lockSubValuesFields(dropFormulaFields(fields))}
        users={users}
        dateFormat={dateFormat}
      />
    );
  };

  return (
    <BulkEditDialog
      title={translate('bulk_changes_custom_fields')}
      size="lg"
      canSave={customFieldsList.length > 0}
      onClose={onClose}
      onSave={handleSave}
      body={renderBody()}
    />
  );
};

export default BulkCustomFieldsDialog;
